import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Building2, Mail, Phone, MapPin, Trash2, Pencil, FileSpreadsheet, FileText, Globe, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { buildBrandedDocument } from "@/lib/documentTemplate";

const empty = { name: "", email: "", phone: "", address: "", gst: "", pan: "", company: "" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const statusColor = (s) => s === "paid" ? "#4A6B53" : s === "overdue" ? "#C0392B" : "#B08D48";

const buildStatementHtml = (data) => {
  const c = data.client, s = data.summary;
  const invRows = data.invoices.map(i => `<tr>
    <td>${i.number || "—"}</td><td>${i.date}</td><td>${i.due_date}</td>
    <td>${i.project_name}</td>
    <td class="right">₹${Number(i.amount).toLocaleString("en-IN")}</td>
    <td class="right">₹${Number(i.tax).toLocaleString("en-IN")}</td>
    <td class="right" style="font-weight:600">₹${Number(i.total).toLocaleString("en-IN")}</td>
    <td style="color:${statusColor(i.status)};text-transform:uppercase;font-size:9px;letter-spacing:1px">${i.status}</td>
  </tr>`).join("");
  const projRows = data.by_project.map(p => `<tr>
    <td>${p.project_name}</td><td class="right">${p.invoice_count}</td>
    <td class="right">₹${Number(p.billed).toLocaleString("en-IN")}</td>
    <td class="right" style="color:#4A6B53">₹${Number(p.collected).toLocaleString("en-IN")}</td>
    <td class="right" style="color:#C0392B">₹${Number(p.outstanding).toLocaleString("en-IN")}</td>
  </tr>`).join("");
  const body = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:12px">
      <div>
        <h1 style="margin:0">${c.name}</h1>
        <div style="color:#73706A;font-size:11px;margin-top:6px">${c.company || ""}${c.company ? " · " : ""}Statement as on ${new Date(data.generated_at).toLocaleDateString("en-IN", { dateStyle: "long" })}</div>
      </div>
      <div style="text-align:right;font-size:10px;color:#73706A;line-height:1.6">
        ${c.email ? `<div>${c.email}</div>` : ""}
        ${c.phone ? `<div>${c.phone}</div>` : ""}
        ${c.gst ? `<div>GST: ${c.gst}</div>` : ""}
        ${c.pan ? `<div>PAN: ${c.pan}</div>` : ""}
      </div>
    </div>
    ${c.address ? `<div style="padding:12px 14px;background:#F7F5F0;border-radius:8px;margin-bottom:16px"><div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#73706A">Billing Address</div><div style="font-size:12px;margin-top:4px">${c.address}</div></div>` : ""}
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:18px">
      <div style="background:#fff;border:1px solid #E2DFD8;border-radius:8px;padding:12px;text-align:center">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#73706A">Invoices</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px">${s.invoice_count}</div>
      </div>
      <div style="background:#fff;border:1px solid #E2DFD8;border-radius:8px;padding:12px;text-align:center">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#73706A">Total Billed</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px">${fmtInr(s.total_billed)}</div>
      </div>
      <div style="background:#fff;border:1px solid #E2DFD8;border-radius:8px;padding:12px;text-align:center">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#4A6B53">Collected</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;color:#4A6B53">${fmtInr(s.total_collected)}</div>
      </div>
      <div style="background:#fff;border:1px solid #E2DFD8;border-radius:8px;padding:12px;text-align:center">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#C0392B">Outstanding</div>
        <div style="font-size:18px;font-weight:700;margin-top:4px;color:#C0392B">${fmtInr(s.total_outstanding)}</div>
      </div>
    </div>
    ${s.total_overdue ? `<div style="background:#FEF2F2;border:1px solid #FCA5A5;color:#991B1B;padding:10px 14px;border-radius:6px;font-size:11px;margin-bottom:20px"><b>Overdue:</b> ${fmtInr(s.total_overdue)} — please settle at the earliest.</div>` : ""}
    ${data.by_project.length ? `<h2>Project-wise Breakdown</h2>
    <table><thead><tr><th>Project</th><th class="right">Invoices</th><th class="right">Billed</th><th class="right">Collected</th><th class="right">Outstanding</th></tr></thead>
    <tbody>${projRows}</tbody></table>` : ""}
    <h2>Invoice Ledger</h2>
    <table><thead><tr><th>Invoice #</th><th>Date</th><th>Due</th><th>Project</th><th class="right">Amount</th><th class="right">Tax</th><th class="right">Total</th><th>Status</th></tr></thead>
    <tbody>${invRows || '<tr><td colspan="8" style="text-align:center;padding:24px;color:#73706A">No invoices issued to this client yet.</td></tr>'}</tbody></table>
  `;
  return buildBrandedDocument(body, {
    subject: `Client Statement — ${c.name}`,
    title: "Client Statement",
    number: c.company || "",
    date: new Date(data.generated_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
  });
};
const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "director"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [portalClient, setPortalClient] = useState(null);
  const [portalPwd, setPortalPwd] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);

  const load = () => api.get("/clients").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name:c.name||"", email:c.email||"", phone:c.phone||"", address:c.address||"", gst:c.gst||"", pan:c.pan||"", company:c.company||"" }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/clients/${editId}`, form); toast.success("Client updated"); }
      else { await api.post("/clients", form); toast.success("Client added"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this client?")) return; await api.delete(`/clients/${id}`); load(); toast.success("Deleted"); };

  const enablePortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post(`/clients/${portalClient.id}/portal/enable`, { password: portalPwd || undefined });
      if (data.generated) toast.success(`Portal enabled · temp password: ${data.password}`, { duration: 12000 });
      else toast.success("Portal access updated");
      setPortalClient(null); setPortalPwd(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setPortalBusy(false); }
  };
  const disablePortal = async (c) => {
    if (!window.confirm("Disable portal access? The client will no longer be able to log in.")) return;
    try { await api.post(`/clients/${c.id}/portal/disable`); toast.success("Portal access disabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openStatementPdf = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.get(`/clients/${c.id}/statement`);
      const w = window.open("", "_blank"); if (!w) { toast.error("Popup blocked — please allow popups"); return; }
      w.document.write(buildStatementHtml(data));
      w.document.close();
      toast.success(`Statement ready · ${data.summary.invoice_count} invoices`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load statement"); }
    finally { setBusyId(null); }
  };

  const downloadStatementXlsx = async (c) => {
    setBusyId(c.id);
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${BACKEND}/api/clients/${c.id}/statement.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `Statement_${c.name.replace(/[^A-Za-z0-9]+/g, "_")}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("XLSX downloaded");
    } catch { toast.error("Failed to download XLSX"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Directory</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-client-btn" onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New Client</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editId ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="col-span-2"><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
              <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <Button onClick={save} data-testid="save-client-btn" className="bg-primary text-primary-foreground">{editId ? "Update" : "Save"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((c) => (
          <div key={c.id} data-testid={`client-${c.id}`} className="rounded-2xl p-6 bg-card border border-border/60 hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-accent/20 flex items-center justify-center border border-border/50">
                <Building2 className="w-5 h-5 text-accent" strokeWidth={1.6} />
              </div>
              <button onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="font-display text-lg font-semibold">{c.name}</div>
              <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
            </div>
            {c.company && <div className="text-xs text-muted-foreground">{c.company}</div>}
            <div className="mt-4 space-y-1.5 text-sm">
              {c.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" />{c.email}</div>}
              {c.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" />{c.phone}</div>}
              {c.address && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-3.5 h-3.5" />{c.address}</div>}
            </div>
            {(c.gst || c.pan) && (
              <div className="mt-4 pt-4 border-t border-border/50 flex gap-4 text-[11px] text-muted-foreground">
                {c.gst && <div><div className="uppercase tracking-[0.15em] text-[9px]">GST</div>{c.gst}</div>}
                {c.pan && <div><div className="uppercase tracking-[0.15em] text-[9px]">PAN</div>{c.pan}</div>}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between gap-2 text-xs">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${c.portal_enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-border/60 text-muted-foreground"}`} data-testid={`portal-status-${c.id}`}>
                <Globe className="w-3 h-3" /> {c.portal_enabled ? "Portal On" : "Portal Off"}
              </span>
              {isAdmin && (
                <button onClick={() => { setPortalClient(c); setPortalPwd(""); }} data-testid={`manage-portal-${c.id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Manage Portal
                </button>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-border/50 flex gap-2">
              <button onClick={() => openStatementPdf(c)} disabled={busyId===c.id} data-testid={`statement-pdf-${c.id}`} className="flex-1 text-xs px-3 py-2 rounded border border-border/60 hover:bg-muted hover:border-primary/40 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50" title="Open printable HTML statement (Print → Save as PDF)"><FileText className="w-3.5 h-3.5" /> Statement (PDF)</button>
              <button onClick={() => downloadStatementXlsx(c)} disabled={busyId===c.id} data-testid={`statement-xlsx-${c.id}`} className="flex-1 text-xs px-3 py-2 rounded border border-border/60 hover:bg-muted hover:border-primary/40 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50" title="Download an Excel statement"><FileSpreadsheet className="w-3.5 h-3.5" /> XLSX</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!portalClient} onOpenChange={(o) => !o && setPortalClient(null)}>
        <DialogContent className="max-w-md" data-testid="portal-dialog">
          <DialogHeader><DialogTitle>Portal Access · {portalClient?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">The client signs in with their email to see only their own projects, invoices and shared documents.</p>
            <div><Label>Login email</Label><Input value={portalClient?.email || ""} readOnly data-testid="portal-email" /></div>
            {!portalClient?.email && <div className="text-xs text-destructive">Add an email to this client first (edit the client) before enabling portal access.</div>}
            <div>
              <Label>{portalClient?.portal_enabled ? "Reset password (optional)" : "Set password (optional)"}</Label>
              <Input type="text" value={portalPwd} onChange={(e) => setPortalPwd(e.target.value)} placeholder="Leave blank to auto-generate" data-testid="portal-password" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={enablePortal} disabled={portalBusy || !portalClient?.email} data-testid="portal-enable-btn" className="bg-primary text-primary-foreground flex-1">
                {portalClient?.portal_enabled ? "Reset / Re-enable" : "Enable Portal"}
              </Button>
              {portalClient?.portal_enabled && (
                <Button variant="outline" onClick={() => disablePortal(portalClient)} data-testid="portal-disable-btn" className="text-destructive">Disable</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
